import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Clock, Calendar, User, Info, Lock, ExternalLink } from 'lucide-react';
import { demoDrops, demoNfts } from '@/services/demoData';
import { motion } from 'framer-motion';

export default function DropDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState('items');
  
  // Use demo data
  const drop = demoDrops[0]; // Using first drop as example
  const dropNfts = demoNfts.filter(nft => nft.dropId === 'demo-drop-1');
  
  return (
    <div className="container-custom py-12 relative">
      {/* 3D Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 to-secondary-500/5 -z-10" />
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Drop Image */}
        <motion.div 
          className="lg:col-span-1"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="sticky top-24">
            <motion.div
              className="rounded-2xl overflow-hidden mb-6 shadow-3d"
              whileHover={{ scale: 1.02, rotateX: 3, rotateY: 3, transition: { duration: 0.3 } }}
            >
              <img 
                src={drop.thumbnailUrl} 
                alt={drop.title}
                className="w-full aspect-video object-cover shadow-inner-3d"
              />
            </motion.div>
            
            <Card className="shadow-3d hover:shadow-3d-hover transition-all duration-300">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Clock className="h-5 w-5 text-primary-500 mr-2" />
                    <span className="text-sm text-shadow">Status</span>
                  </div>
                  <span className="text-sm font-medium px-2 py-1 bg-primary-500/20 text-primary-400 rounded-full shadow-3d">
                    {drop.isActive ? 'Active' : 'Ended'}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Calendar className="h-5 w-5 text-primary-500 mr-2" />
                    <span className="text-sm text-shadow">Start Date</span>
                  </div>
                  <span className="text-sm text-shadow">
                    {new Date(drop.startTime).toLocaleDateString()}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Calendar className="h-5 w-5 text-primary-500 mr-2" />
                    <span className="text-sm text-shadow">End Date</span>
                  </div>
                  <span className="text-sm text-shadow">
                    {new Date(drop.endTime).toLocaleDateString()}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Info className="h-5 w-5 text-primary-500 mr-2" />
                    <span className="text-sm text-shadow">Items</span>
                  </div>
                  <span className="text-sm font-medium text-shadow">{drop.itemCount}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <User className="h-5 w-5 text-primary-500 mr-2" />
                    <span className="text-sm text-shadow">Creator</span>
                  </div>
                  <span className="text-sm font-medium text-shadow">{drop.creatorName}</span>
                </div>
                
                {drop.isTokenGated && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Lock className="h-5 w-5 text-primary-500 mr-2" />
                      <span className="text-sm text-shadow">Access</span>
                    </div>
                    <span className="text-sm text-shadow">Token Gated</span>
                  </div>
                )}
                
                <div className="pt-4 border-t">
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button className="w-full shadow-3d hover:shadow-3d-hover transform hover:scale-105 transition-all duration-300">
                      View on Explorer <ExternalLink className="h-4 w-4 ml-2" />
                    </Button>
                  </motion.div>
                </div>
              </CardContent>
            </Card>
          </div>
        </motion.div>
        
        {/* Right Column - Drop Details */}
        <motion.div 
          className="lg:col-span-2"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <h1 className="text-3xl font-bold mb-2 text-shadow">{drop.title}</h1>
          
          <div className="flex items-center mb-6">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500/20 to-secondary-500/20 mr-2 shadow-3d">
              <img 
                src={`https://picsum.photos/seed/${drop.creatorName}/40/40`}
                alt={drop.creatorName}
                className="w-full h-full rounded-full object-cover"
              />
            </div>
            <span className="text-sm font-medium text-shadow">{drop.creatorName}</span>
          </div>
          
          <p className="text-gray-400 mb-8">{drop.description}</p>
          
          <Tabs defaultValue="items" className="mb-8">
            <TabsList className="shadow-3d">
              <TabsTrigger 
                value="items" 
                onClick={() => setActiveTab('items')}
                className="hover:shadow-3d-hover transition-all duration-300"
              >
                Items
              </TabsTrigger>
              <TabsTrigger 
                value="activity" 
                onClick={() => setActiveTab('activity')}
                className="hover:shadow-3d-hover transition-all duration-300"
              >
                Activity
              </TabsTrigger>
              <TabsTrigger 
                value="details" 
                onClick={() => setActiveTab('details')}
                className="hover:shadow-3d-hover transition-all duration-300"
              >
                Details
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="items" className="mt-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {dropNfts.map((nft, index) => (
                  <motion.div 
                    key={nft.id} 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="overflow-hidden rounded-lg shadow-3d"
                    whileHover={{ scale: 1.03, rotateX: 2, rotateY: 2, transition: { duration: 0.3 } }}
                  >
                    <Card className="overflow-hidden h-full shadow-3d hover:shadow-3d-hover transition-all duration-300">
                      <div className="aspect-square bg-dark-700">
                        <img 
                          src={nft.image} 
                          alt={nft.name}
                          className="w-full h-full object-cover shadow-inner-3d transition-transform duration-300 hover:scale-105"
                        />
                      </div>
                      <CardContent className="p-4">
                        <h3 className="font-medium mb-1 heading-gradient text-shadow">{nft.name}</h3>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-400">#{nft.tokenId}</span>
                          <span className="text-primary-500 font-medium text-shadow">{nft.price} DUSD</span>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="activity" className="mt-6">
              <Card className="shadow-3d hover:shadow-3d-hover transition-all duration-300">
                <CardContent className="p-4">
                  <div className="space-y-4">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <motion.div 
                        key={i}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: i * 0.1 }}
                        className="flex items-start pb-4 border-b last:border-0 last:pb-0"
                      >
                        <div className="w-10 h-10 rounded-full overflow-hidden mr-4 flex-shrink-0 shadow-3d">
                          <img 
                            src={`https://picsum.photos/seed/user${i}/40/40`}
                            alt="User" 
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div>
                          <p className="text-sm">
                            <span className="font-medium text-shadow">User{i}</span>
                            {' '}
                            {i % 3 === 0 ? 'purchased' : i % 3 === 1 ? 'minted' : 'bid on'} 
                            {' '}
                            <span className="font-medium text-shadow">Item #{i}</span>
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {i} day{i !== 1 ? 's' : ''} ago
                          </p>
                        </div>
                        <div className="ml-auto text-right">
                          <p className="text-sm font-medium text-shadow">{(i * 0.1).toFixed(1)} DUSD</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="details" className="mt-6">
              <Card className="shadow-3d hover:shadow-3d-hover transition-all duration-300">
                <CardContent className="p-6">
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-medium mb-2 text-shadow">About this Drop</h3>
                      <p className="text-gray-400">
                        {drop.description || "This is a limited edition collection of digital art pieces created exclusively for FTTF.World. Each piece is unique and represents a different aspect of the digital metaverse."}
                      </p>
                    </div>
                    
                    <div className="pt-4 border-t">
                      <h3 className="text-lg font-medium mb-2 text-shadow">Contract Details</h3>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Contract Address</span>
                          <span className="text-sm font-mono text-shadow">0x0e694875e9376A00A8222CD4E769b61B4416CD43</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Token Standard</span>
                          <span className="text-shadow">ERC-721</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Chain</span>
                          <span className="text-shadow">Sonic Blaze Testnet</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Creator Royalties</span>
                          <span className="text-shadow">10%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </div>
  );
} 